import { useState } from "react"
import { Button, Input, Textarea } from "@aix/ui"
import { X, Sparkles } from "lucide-react"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import { useNavigate } from "@tanstack/react-router"

interface CreateFromTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  template: {
    id: string
    name: string
    icon: string
    description: string | null
    type: "pillar" | "side-quest"
    defaultTone: "tough-love" | "chill" | "science-y"
    defaultDurationWeeks: number | null
  }
}

export default function CreateFromTemplateModal({ isOpen, onClose, template }: CreateFromTemplateModalProps) {
  const navigate = useNavigate()
  const [name, setName] = useState(template.name)
  const [icon, setIcon] = useState(template.icon)
  const [tone, setTone] = useState<"tough-love" | "chill" | "science-y">(template.defaultTone)
  const [description, setDescription] = useState(template.description || "")
  const [startDate, setStartDate] = useState("")
  const [targetEndDate, setTargetEndDate] = useState("")

  const createMutation = trpc.coaches.createFromTemplate.useMutation()

  const handleCreate = async () => {
    try {
      // Validate required fields for side quests
      if (template.type === "side-quest" && !targetEndDate) {
        toast.error("Side quest coaches must have a target end date")
        return
      }

      const result = await createMutation.mutateAsync({
        templateId: template.id,
        customizations: {
          name: name !== template.name ? name : undefined,
          icon: icon !== template.icon ? icon : undefined,
          tone: tone !== template.defaultTone ? tone : undefined,
          description: description !== (template.description || "") ? description : undefined,
          startDate: startDate || undefined,
          targetEndDate: targetEndDate || undefined,
        },
      })

      toast.success(`Coach "${name}" created successfully!`)
      onClose()

      // Navigate to the new coach
      navigate({
        to: "/coach-os/coach/$coachId",
        // @ts-ignore
        params: { coachId: result.coach.id },
      })
    } catch (error: any) {
      toast.error(error.message || "Failed to create coach")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border-4 border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b-2 border-border p-4 flex items-center justify-between bg-secondary sticky top-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-primary">Create Coach from Template</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-background rounded-md transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template Preview */}
        <div className="p-4 border-b-2 border-border bg-secondary/50">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{template.icon}</span>
            <div>
              <h3 className="font-bold text-lg">{template.name}</h3>
              <p className="text-sm text-muted-foreground">
                {template.type === "pillar" ? "🏛️ Pillar Habit" : "🎯 Side Quest"}
                {template.defaultDurationWeeks && ` • ${template.defaultDurationWeeks} weeks`}
              </p>
            </div>
          </div>
          {template.description && <p className="text-sm text-muted-foreground mt-2">{template.description}</p>}
        </div>

        {/* Customization Form */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Customize your coach before creating it. You can leave fields unchanged to use template defaults.
          </p>

          {/* Name */}
          <div>
            <label className="text-sm font-medium block mb-1">Coach Name *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Half Marathon Journey"
              className="w-full"
            />
          </div>

          {/* Icon */}
          <div>
            <label className="text-sm font-medium block mb-1">Icon (emoji)</label>
            <Input
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="🏃"
              className="w-full"
              maxLength={4}
            />
          </div>

          {/* Tone */}
          <div>
            <label className="text-sm font-medium block mb-2">Coach Tone</label>
            <div className="flex gap-2">
              {(["tough-love", "chill", "science-y"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`px-4 py-2 rounded-md text-sm font-medium border-2 transition-colors ${
                    tone === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:bg-secondary"
                  }`}
                >
                  {t === "tough-love" && "💪 Tough Love"}
                  {t === "chill" && "😎 Chill"}
                  {t === "science-y" && "🔬 Science-y"}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium block mb-1">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add your own notes or modify the template description..."
              className="w-full"
              rows={3}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">
                Start Date {template.type === "pillar" ? "(optional)" : ""}
              </label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">
                Target End Date {template.type === "side-quest" ? "*" : "(optional)"}
              </label>
              <Input
                type="date"
                value={targetEndDate}
                onChange={(e) => setTargetEndDate(e.target.value)}
                className="w-full"
              />
              {template.type === "side-quest" && !targetEndDate && (
                <p className="text-xs text-destructive mt-1">Required for side quests</p>
              )}
            </div>
          </div>

          {template.type === "side-quest" && template.defaultDurationWeeks && (
            <div className="bg-secondary/50 border-2 border-border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                💡 Tip: This template is designed for a {template.defaultDurationWeeks}-week program. Consider setting
                your target end date approximately {template.defaultDurationWeeks} weeks from now.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t-2 border-border p-4 flex gap-3 justify-end bg-secondary">
          <Button onClick={onClose} variant="outline" disabled={createMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-primary text-primary-foreground"
            disabled={!name.trim() || createMutation.isLoading}
            loading={createMutation.isLoading}
          >
            {createMutation.isLoading ? "Creating..." : "Create Coach"}
          </Button>
        </div>
      </div>
    </div>
  )
}
